import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, FileText, Grid3X3, Image as ImageIcon, Link2 } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import { cn } from "@/lib/utils";
import { INITIAL_ACHIEVEMENTS, achievementTypeLabel } from "../achievements/mock";

export default function KnowledgeAchievementPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const item = useMemo(() => INITIAL_ACHIEVEMENTS.find((a) => a.id === id) ?? null, [id]);

  if (!item) {
    return (
      <div className="flex h-screen overflow-hidden bg-white text-foreground">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex min-w-0 flex-1 items-center justify-center">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">成果不存在或链接已失效。</div>
            <button
              type="button"
              onClick={() => navigate("/knowledge")}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              <ArrowLeft className="h-4 w-4" />
              返回
            </button>
          </div>
        </main>
      </div>
    );
  }

  const headerBtn =
    "inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent";

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="border-b border-border bg-white/90 backdrop-blur">
          <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center gap-3 px-5 md:px-6 lg:px-8">
            <button type="button" onClick={() => navigate(-1)} className={headerBtn}>
              <ArrowLeft className="h-4 w-4" />
              返回
            </button>
            <span className="text-muted-foreground">/</span>
            <div className="min-w-0">
              <div className="truncate text-base font-semibold tracking-tight text-foreground">{item.title}</div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {achievementTypeLabel[item.type]} · {item.date}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" className={headerBtn}>
                <Link2 className="h-4 w-4" />
                复制链接
              </button>
              <button type="button" className={headerBtn}>
                <Download className="h-4 w-4" />
                下载
              </button>
              <button type="button" className={cn(headerBtn, "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10")}>
                <ExternalLink className="h-4 w-4" />
                打开可视化
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1100px] space-y-5">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="text-sm font-semibold text-foreground">项目概述</div>
              <div className="mt-2 text-sm leading-7 text-muted-foreground">{item.desc}</div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {[
                  { k: "版本", v: "v2.1" },
                  { k: "数据规模", v: "5 篇核心文献 + 2 套基线" },
                  { k: "关键指标", v: "错误率 / 资源开销 / 延迟" },
                ].map((m) => (
                  <div key={m.k} className="rounded-xl border border-border bg-muted/10 p-4">
                    <div className="text-xs font-medium text-muted-foreground">{m.k}</div>
                    <div className="mt-2 text-sm font-semibold text-foreground">{m.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {item.type === "pdf" && <PdfProject />}
            {item.type === "html" && <HtmlProject />}
            {item.type === "图片" && <ImagesProject />}
            {item.type === "word" && <DocxProject />}
            {item.type === "ppt" && <PptProject />}
          </div>
        </div>
      </main>
    </div>
  );
}

function PdfProject() {
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
      <aside className="rounded-2xl border border-border bg-card p-5 shadow-sm lg:sticky lg:top-6 lg:h-fit">
        <div className="text-sm font-semibold text-foreground">目录</div>
        <div className="mt-3 space-y-2 text-sm">
          {["摘要与关键结论", "方法与数据来源", "结果可视化", "复现实验与附录"].map((t, i) => (
            <div key={t} className="flex items-center justify-between rounded-xl border border-border bg-muted/10 px-3 py-2">
              <span className="text-foreground">{t}</span>
              <span className="text-xs text-muted-foreground">P{i + 1}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-border bg-primary/5 p-4">
          <div className="text-xs font-medium text-primary">综合评分</div>
          <div className="mt-2 text-2xl font-semibold text-foreground">0.82</div>
          <div className="mt-1 text-xs text-muted-foreground">CI 0.78–0.86</div>
        </div>
      </aside>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-foreground">报告预览</div>
          <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">PDF</span>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            { title: "第 1 页 · 摘要与关键结论", points: ["综合评分 0.82", "错误率降低 23%", "建议验证 3 个条件"] },
            { title: "第 2 页 · 方法与数据来源", points: ["数据：论文+开源实现", "基线：Surface/Bacon–Shor", "指标：错误率/延迟"] },
            { title: "第 3 页 · 结果可视化", points: ["P95 延迟 -18ms", "资源开销 +6%", "稳定性 std ↓12%"] },
            { title: "第 4 页 · 复现实验与附录", points: ["环境：Python 3.11", "seed：20260415", "脚本：run_eval.py"] },
          ].map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-background p-4">
              <div className="text-xs font-semibold text-foreground">{p.title}</div>
              <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                {p.points.map((x) => (
                  <div key={x} className="flex gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                    <span className="leading-relaxed">{x}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-border bg-muted/10 p-3">
                <div className="text-[10px] font-medium text-muted-foreground">图表</div>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {[68, 82, 74, 91].map((v) => (
                    <div key={v} className="flex items-end gap-2 rounded-md bg-background p-2">
                      <div className="h-10 w-2 rounded bg-muted/50" />
                      <div className="w-2 rounded bg-primary/70" style={{ height: `${Math.round((v / 100) * 40)}px` }} />
                      <div className="ml-auto text-[10px] font-semibold text-foreground">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function HtmlProject() {
  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto; margin: 0; background: #f7f8f8; color: #1d2129; }
    header { background: #fff; border-bottom: 1px solid #e5e6eb; padding: 12px 16px; position: sticky; top: 0; }
    .wrap { padding: 16px; display: grid; gap: 12px; grid-template-columns: 1fr 1fr 1fr; }
    .card { background: #fff; border: 1px solid #e5e6eb; border-radius: 14px; padding: 12px; }
    .muted { color: #646479; font-size: 12px; }
    .big { font-size: 22px; font-weight: 700; margin-top: 6px; }
    .bar { height: 8px; background: rgba(61,105,232,.12); border-radius: 999px; overflow: hidden; margin-top: 10px; }
    .bar > div { height: 100%; width: 66%; background: rgba(61,105,232,.85); }
  </style>
</head>
<body>
  <header>
    <div style="font-weight:700;">材料筛选可视化（演示项目）</div>
    <div class="muted" style="margin-top:4px;">候选材料：214 · 有效样本：198 · 更新时间：2026-04-08</div>
  </header>
  <div class="wrap">
    <div class="card"><div class="muted">Top-1 候选评分</div><div class="big">0.91</div><div class="bar"><div></div></div></div>
    <div class="card"><div class="muted">通过约束比例</div><div class="big">62%</div><div class="bar"><div style="width:62%"></div></div></div>
    <div class="card"><div class="muted">平均计算耗时</div><div class="big">3.4m</div><div class="bar"><div style="width:48%"></div></div></div>
  </div>
</body>
</html>`;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-foreground">完整可视化页面（演示）</div>
        <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">HTML</span>
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-background">
        <iframe title="html-achievement-full" className="h-[620px] w-full" sandbox="allow-scripts" srcDoc={html} />
      </div>
    </div>
  );
}

function ImagesProject() {
  const imgs = [
    { title: "能量分布热图", note: "T=300K，采样 50k steps" },
    { title: "轨迹片段（3D）", note: "局部重排与缺陷迁移" },
    { title: "RDF 曲线对比", note: "A/B 参数对结构差异" },
    { title: "应力-应变曲线", note: "屈服点附近波动" },
    { title: "聚类结果", note: "K=6，轮廓系数 0.41" },
    { title: "参数敏感性", note: "对截断半径最敏感" },
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Grid3X3 className="h-4 w-4 text-primary" />
          图集
        </div>
        <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">Images</span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {imgs.map((im, i) => (
          <div key={im.title} className="overflow-hidden rounded-xl border border-border bg-background">
            <div
              className="aspect-[4/3]"
              style={{
                background:
                  i % 3 === 0
                    ? "linear-gradient(135deg, rgba(61,105,232,.22), rgba(61,105,232,0))"
                    : i % 3 === 1
                      ? "linear-gradient(135deg, rgba(61,105,232,.12), rgba(61,105,232,.02))"
                      : "linear-gradient(135deg, rgba(61,105,232,.18), rgba(61,105,232,0))",
              }}
            />
            <div className="border-t border-border p-3">
              <div className="text-xs font-semibold text-foreground">
                {i + 1}. {im.title}
              </div>
              <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{im.note}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocxProject() {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FileText className="h-4 w-4 text-primary" />
          文档预览
        </div>
        <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">DOCX</span>
      </div>
      <div className="mt-4 rounded-2xl border border-border bg-background p-5">
        <div className="text-base font-semibold text-foreground">实验记录与参数表</div>
        <div className="mt-1 text-sm text-muted-foreground">记录编号：EXP-2026-0406 · 版本：v1.3</div>
        <p className="mt-4 text-sm leading-7 text-muted-foreground">
          本文档汇总了关键实验条件、仪器参数与异常记录，用于组内复核与归档。建议在每次跑完实验后补充“偏差原因”和“下一步验证”两列，便于形成可追溯闭环。
        </p>
        <div className="mt-5 overflow-hidden rounded-xl border border-border">
          <div className="grid grid-cols-4 gap-0 border-b border-border bg-muted/20 px-3 py-2 text-xs font-medium text-muted-foreground">
            <div>参数</div>
            <div>设定值</div>
            <div>实测值</div>
            <div>备注</div>
          </div>
          {[
            ["温度", "300K", "301.2K", "稳定 ±0.6K"],
            ["压力", "1 atm", "0.98 atm", "泵压波动"],
            ["步长", "1 fs", "1 fs", "固定"],
            ["采样", "50k steps", "48.7k", "中途重启一次"],
          ].map((row) => (
            <div key={row[0]} className="grid grid-cols-4 gap-0 px-3 py-2 text-xs text-foreground">
              <div className="text-muted-foreground">{row[0]}</div>
              <div>{row[1]}</div>
              <div className="font-medium">{row[2]}</div>
              <div className="text-muted-foreground">{row[3]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PptProject() {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ImageIcon className="h-4 w-4 text-primary" />
          Slide 预览
        </div>
        <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">PPT</span>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {[
          { t: "研究进展概览", b: ["里程碑完成度 72%", "P95 延迟 180ms", "下一步：补齐对照实验"] },
          { t: "关键实验结果", b: ["逻辑错误率下降 23%", "资源开销 +6%", "稳定性 std ↓ 12%"] },
          { t: "风险与对策", b: ["合规条款需审查", "样本口径需统一", "冻结指标口径"] },
          { t: "计划与排期", b: ["本周：复现实验", "下周：补齐图表", "月底：输出终稿"] },
        ].map((s, i) => (
          <div key={s.t} className="overflow-hidden rounded-xl border border-border bg-background">
            <div className="aspect-[16/9] bg-muted/10 p-4">
              <div className="text-xs font-semibold text-foreground">Slide {i + 1}</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{s.t}</div>
              <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                {s.b.map((x) => (
                  <div key={x} className="flex gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                    <span className="leading-relaxed">{x}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-border p-3">
              <div className="text-xs font-medium text-muted-foreground">备注：可接入真实 pptx 渲染</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

